"use client";
import Image from "next/image";
import DualRangeSlider from "@/components/Common/DualRangeSlider";
import DurationFilter from "@/components/Common/DurationFilter";
import FilterIcon from "../SVG/FilterIcon";
import {
  ALL_LOCATIONS,
  CATALOG_FORMAT_MODES,
  FINANCING_OPTIONS,
  PRICE_MAX,
  PRICE_MIN,
  type CatalogFiltersController,
} from "@/hooks/useCatalogFilters";
import { useState } from "react";

interface Props {
  /** Filter state + handlers from `useCatalogFilters`. */
  controller: CatalogFiltersController;
}

/**
 * Desktop filter sidebar shared by the main formations catalog and the
 * entrepreneurship catalog: mode/format pills, price range, financing,
 * duration and location sections. Hidden below the `md` breakpoint — the
 * mobile equivalent is the shared FilterDrawer.
 */
export default function CatalogFilterSidebar({ controller }: Props) {
  const {
    selectedFormatMode,
    setSelectedFormatMode,
    selectedFinancing,
    handleToggleFinancing,
    selectedLocations,
    handleToggleLocation,
    priceRange,
    setPriceRange,
    isPriceOpen,
    setIsPriceOpen,
    isFinanceOpen,
    setIsFinanceOpen,
    isLocationOpen,
    setIsLocationOpen,
    handleResetFilters,
  } = controller;

  const [isFormatModeOpen, setIsFormatModeOpen] = useState(true);

  return (
    <div className="hidden md:block">
      <div className="flex items-center justify-between gap-3 mb-8">
        <div className="flex items-center gap-3">
          <FilterIcon
            className="w-6 h-6"
            circleColor="#1B365E"
            strokeColor="#ffffff"
          />
          <p className="text-[#1B365E] text-[25px] leading-10 font-light font-red-hat">
            Filtrez
          </p>
        </div>
        <button
          type="button"
          onClick={handleResetFilters}
          aria-label="Réinitialiser les filtres"
        >
          <Image
            className="min-w-6 w-6"
            src="/assets/icons/reload.svg"
            alt="reload"
            width={24}
            height={24}
          />
        </button>
      </div>

      <div className="flex flex-col gap-6 text-[#1D1D1D]">
        {/* Mode pills */}
        {/* <div className="flex flex-wrap gap-4">
          {CATALOG_MODES.map((label) => {
            const active = selectedMode === label;
            return (
              <button
                key={label}
                onClick={() => setSelectedMode(active ? null : label)}
                className={`cursor-pointer text-xs font-bold px-6 py-3.75 rounded-full border transition-colors ${
                  active
                    ? "bg-[#1B365E] text-white border-[#1B365E]"
                    : "bg-white text-[#1B365E] border-[#EFEFEF] hover:bg-[#F5FBFD]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div> */}

        {/* Format mode */}
        <div className="pt-6 border-t border-[#EFEFEF]">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">
              Format
            </p>

            <button
              type="button"
              aria-label={isFormatModeOpen ? "reduce" : "expand"}
              onClick={() => setIsFormatModeOpen((v) => !v)}
              className="cursor-pointer w-4 h-4"
            >
              <Image
                className="w-4 shrink-0"
                src={
                  isFormatModeOpen
                    ? "/assets/icons/minus.svg"
                    : "/assets/icons/plus.svg"
                }
                alt={isFormatModeOpen ? "minus" : "plus"}
                width={16}
                height={16}
              />
            </button>
          </div>

          {isFormatModeOpen && (
            <div className="flex flex-wrap gap-4">
              {CATALOG_FORMAT_MODES.map((label) => {
                const active = selectedFormatMode === label;

                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setSelectedFormatMode(active ? null : label)}
                    className={`cursor-pointer text-xs font-bold px-6 py-3.75 rounded-full border transition-colors ${
                      active
                        ? "bg-[#1B365E] text-white border-[#1B365E]"
                        : "bg-white text-[#1B365E] border-[#EFEFEF] hover:bg-[#F5FBFD]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Prix */}
        <div className="pt-6 border-t border-[#EFEFEF]">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">
              Prix
            </p>
            <button
              type="button"
              aria-label={isPriceOpen ? "reduce" : "expand"}
              onClick={() => setIsPriceOpen((v) => !v)}
              className="cursor-pointer w-4 h-4"
            >
              <Image
                className="w-4 shrink-0"
                src={
                  isPriceOpen
                    ? "/assets/icons/minus.svg"
                    : "/assets/icons/plus.svg"
                }
                alt={isPriceOpen ? "minus" : "plus"}
                width={16}
                height={16}
              />
            </button>
          </div>
          {isPriceOpen && (
            <div className="px-1">
              <DualRangeSlider
                min={PRICE_MIN}
                max={PRICE_MAX}
                step={10}
                value={priceRange}
                onChange={setPriceRange}
              />
              <p className="text-sm mt-3">
                Prix : de {priceRange[0]}€ à {priceRange[1]}€
              </p>
            </div>
          )}
        </div>

        {/* Financement */}
        <div className="pt-6 border-t border-[#EFEFEF]">
          <div className="flex items-center justify-between gap-4 mb-[18px]">
            <p className="text-[15px] font-bold leading-8 hanken-grotesk tracking-[0.2px]">
              Financement
            </p>
            <button
              type="button"
              aria-label={isFinanceOpen ? "reduce" : "expand"}
              onClick={() => setIsFinanceOpen((v) => !v)}
              className="cursor-pointer w-4 h-4"
            >
              <Image
                className="w-4 shrink-0"
                src={
                  isFinanceOpen
                    ? "/assets/icons/minus.svg"
                    : "/assets/icons/plus.svg"
                }
                alt={isFinanceOpen ? "minus" : "plus"}
                width={16}
                height={16}
              />
            </button>
          </div>
          {isFinanceOpen && (
            <div className="flex flex-col gap-6 text-sm">
              {FINANCING_OPTIONS.map((f) => (
                <label
                  key={f}
                  className="inline-flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={selectedFinancing.includes(f)}
                    onChange={() => handleToggleFinancing(f)}
                    className="cursor-pointer w-6 h-6 rounded border border-[#ABABAB] accent-[#1B365E]"
                  />
                  <span className="text-sm">{f}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Durée */}
        <DurationFilter
          controller={controller}
          className="pt-6 border-t border-[#EFEFEF]"
          groupName="duration-sidebar"
        />

        {/* Lieu de formations */}
        <div className="pt-6 border-t border-[#EFEFEF]">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="text-[15px] font-bold leading-4.5 hanken-grotesk tracking-[0.2px]">
              Lieu de formations
            </p>
            <button
              type="button"
              aria-label={isLocationOpen ? "reduce" : "expand"}
              onClick={() => setIsLocationOpen((v) => !v)}
              className="cursor-pointer w-4 h-4"
            >
              <Image
                className="w-4 shrink-0"
                src={
                  isLocationOpen
                    ? "/assets/icons/minus.svg"
                    : "/assets/icons/plus.svg"
                }
                alt={isLocationOpen ? "minus" : "plus"}
                width={16}
                height={16}
              />
            </button>
          </div>
          {isLocationOpen && (
            <div className="flex flex-col gap-3.5">
              {ALL_LOCATIONS.map((loc) => (
                <label
                  key={loc}
                  className="flex items-center gap-2.5 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={selectedLocations.includes(loc)}
                    onChange={() => handleToggleLocation(loc)}
                    className="cursor-pointer w-3.5 h-3.5 rounded border border-[#ABABAB] accent-[#1B365E] shrink-0"
                  />
                  <span
                    className={`text-sm leading-5 ${loc === "À distance" ? "text-[#1B365E] font-medium" : "text-[#1D1D1D]"}`}
                  >
                    {loc}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
